export const MODERATION_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

export const REPORT_STATUS = {
  OPEN: "open",
  UNDER_REVIEW: "under_review",
  RESOLVED: "resolved",
  DISMISSED: "dismissed",
} as const;

export const isPendingModeration = (status: string) =>
  status === MODERATION_STATUS.PENDING;

export const MODERATION_BULK_ACTION = {
  APPROVE: "approve",
  REJECT: "reject",
} as const;

export type ModerationBulkAction =
  (typeof MODERATION_BULK_ACTION)[keyof typeof MODERATION_BULK_ACTION];

export const CONTENT_TYPES = [
  { value: "", label: "All Content Types" },
  { value: "post", label: "Posts" },
  { value: "comment", label: "Comments" },
  { value: "review", label: "Reviews" },
  { value: "business_profile", label: "Business Profiles" },
  { value: "product", label: "Products" },
  { value: "product_image", label: "Product Images" },
] as const;

export const MODERATION_STATUSES = [
  { value: "", label: "All Status" },
  { value: MODERATION_STATUS.PENDING, label: "Pending" },
  { value: MODERATION_STATUS.APPROVED, label: "Approved" },
  { value: MODERATION_STATUS.REJECTED, label: "Rejected" },
] as const;

export const REPORT_CATEGORIES = [
  { value: "", label: "All Categories" },
  { value: "spam", label: "Spam" },
  { value: "offensive", label: "Offensive" },
  { value: "false_advertising", label: "False Advertising" },
  { value: "harassment", label: "Harassment" },
  { value: "impersonation", label: "Impersonation" },
  { value: "other", label: "Other" },
] as const;

export const REPORT_STATUSES = [
  { value: "", label: "All Status" },
  { value: REPORT_STATUS.OPEN, label: "Open" },
  { value: REPORT_STATUS.UNDER_REVIEW, label: "Under Review" },
  { value: REPORT_STATUS.RESOLVED, label: "Resolved" },
  { value: REPORT_STATUS.DISMISSED, label: "Dismissed" },
] as const;

export const RESTRICTION_TYPES = [
  { value: "warning", label: "Warning" },
  { value: "content_posting_banned", label: "Content Posting Banned" },
  { value: "commenting_banned", label: "Commenting Banned" },
  { value: "messaging_restricted", label: "Messaging Restricted" },
  { value: "account_suspended", label: "Account Suspended" },
] as const;

export const moderationStatusColors: Record<string, string> = {
  [MODERATION_STATUS.PENDING]: "light-warning",
  [MODERATION_STATUS.APPROVED]: "light-success",
  [MODERATION_STATUS.REJECTED]: "light-danger",
};

export const reportStatusColors: Record<string, string> = {
  [REPORT_STATUS.OPEN]: "light-danger",
  [REPORT_STATUS.UNDER_REVIEW]: "light-warning",
  [REPORT_STATUS.RESOLVED]: "light-success",
  [REPORT_STATUS.DISMISSED]: "light-secondary",
};

export const reportCategoryColors: Record<string, string> = {
  spam: "light-secondary",
  offensive: "light-danger",
  false_advertising: "light-warning",
  harassment: "light-danger",
  impersonation: "light-info",
  other: "light-secondary",
};

export const contentTypeColors: Record<string, string> = {
  post: "light-primary",
  comment: "light-info",
  review: "light-warning",
  business_profile: "light-success",
  product: "light-secondary",
  product_image: "light-primary",
};

export const MODERATION_ACTION = {
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
} as const;

export const MODERATION_CONTENT_TYPE = {
  POST: "post",
  COMMENT: "comment",
  REVIEW: "review",
  BUSINESS_PROFILE: "business_profile",
  PRODUCT: "product",
  PRODUCT_IMAGE: "product_image",
} as const;

export const MODERATION_ENTITY_TYPE = {
  BUSINESS: "business",
  BOOKING: "booking",
  PRODUCT: "product",
} as const;

export const MODERATION_ACCOUNT_STATUS = {
  ACTIVE: "active",
} as const;

export const moderationActionColors: Record<string, string> = {
  [MODERATION_ACTION.CREATE]: "light-success",
  [MODERATION_ACTION.UPDATE]: "light-warning",
  [MODERATION_ACTION.DELETE]: "light-danger",
};

export const formatModerationLabel = (value: string): string => {
  if (!value) return "N/A";
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const formatEntityTableLabel = (entityTable: string): string => {
  if (!entityTable) return "N/A";
  const name = entityTable.replace(/^app_/i, "").replace(/_/g, " ");
  return formatModerationLabel(name);
};

export const isOpenReport = (status: string) =>
  status === REPORT_STATUS.OPEN || status === REPORT_STATUS.UNDER_REVIEW;

export const canMarkReportUnderReview = (status: string) =>
  status === REPORT_STATUS.OPEN;

export const canTakeReportAction = (status: string) => isOpenReport(status);

export type ReportPendingAction =
  | typeof REPORT_STATUS.UNDER_REVIEW
  | typeof REPORT_STATUS.DISMISSED
  | "remove"
  | "restrict"
  | "ban"
  | null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Name from nested person objects (author / reviewer / user). */
export const getModerationPersonName = (
  person?: Record<string, unknown> | null
): string => {
  if (!person) return "";
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (typeof person.fullName === "string" && person.fullName.trim()) {
    return person.fullName.trim();
  }
  if (typeof person.name === "string" && person.name.trim()) {
    return person.name.trim();
  }
  if (typeof person.email === "string" && person.email.trim()) {
    return person.email.trim();
  }
  return "";
};

/**
 * Resolve "Submitted By" as the content author (not the admin/moderator).
 * Checks author / reviewer / user nested on entity, payload, or list row.
 */
export const resolveModerationAuthorName = (
  ...sources: Array<unknown>
): string => {
  const personKeys = [
    "author",
    "reviewer",
    "user",
    "owner",
    "contentOwner",
    "createdByUser",
    "submitter",
  ];

  for (const source of sources) {
    if (!isRecord(source)) continue;

    for (const key of personKeys) {
      const person = source[key];
      if (isRecord(person)) {
        const name = getModerationPersonName(person);
        if (name) return name;
      }
    }

    // Nested post.author (comments)
    if (isRecord(source.post) && isRecord(source.post.author)) {
      const name = getModerationPersonName(source.post.author);
      if (name) return name;
    }

    // Flat author fields on the row / payload
    if (source.firstName != null || source.lastName != null) {
      const name = getModerationPersonName(source);
      if (name) return name;
    }

    for (const key of ["authorName", "userName", "submittedByUserName"]) {
      if (typeof source[key] === "string" && source[key].trim()) {
        return source[key].trim();
      }
    }
  }

  return "";
};

/** Display label for queue/detail Submitted By — author first, then API fallback. */
export const getModerationSubmittedByLabel = (
  data: {
    submittedByName?: string | null;
    submittedBy?: number | null;
    entityData?: Record<string, unknown> | null;
    payload?: Record<string, unknown> | null;
    previousPayload?: Record<string, unknown> | null;
    author?: Record<string, unknown> | null;
  },
  fallback = "N/A"
): string => {
  const fromAuthor = resolveModerationAuthorName(
    data.entityData,
    data.payload,
    data.previousPayload,
    data.author,
    data
  );
  if (fromAuthor) return fromAuthor;

  if (typeof data.submittedByName === "string" && data.submittedByName.trim()) {
    return data.submittedByName.trim();
  }

  if (data.submittedBy != null) return `User #${data.submittedBy}`;
  return fallback;
};

export const getModerationContentType = (
  contentType: string,
  entityTable: string
): string => {
  const normalizedType = contentType?.trim();
  if (normalizedType) {
    return normalizedType;
  }

  if (entityTable) {
    return inferContentTypeFromEntityTable(entityTable);
  }

  return "post";
};

export const inferContentTypeFromEntityTable = (
  entityTable: string,
  entityData?: Record<string, unknown> | null
): string => {
  const table = entityTable.toLowerCase();
  if (table.includes("post")) return "post";
  if (table.includes("comment")) return "comment";
  if (table.includes("review")) return "review";
  if (table.includes("business")) return "business_profile";
  if (table.includes("product_image") || table.includes("image")) return "product_image";
  if (table.includes("product")) return "product";
  if (entityData?.post || entityData?.author) return "comment";
  if (entityData?.title != null && entityData?.postType != null) return "post";
  if (entityData?.rating != null) return "review";
  if (entityData?.comment || entityData?.reviewText) return "review";
  if (entityData?.reviewType) return "review";
  if (entityData?.businessName) return "business_profile";
  return "post";
};
